import React, { useState } from 'react';

import { AnnouncementStatusDialog } from '../../employee-messages/announcement-composer/announcement-status-dialog/announcement-status-dialog';
import { ProductTelemetryController } from '../../product-telemetry/product-telemetry-controller';
import { TwilioErrorDetailDialog } from '../legacy-components/text-message-errors/twilio-error-detail-dialog';

import { failedAnnouncementErrorThreshold } from './message-item';

import { humanize } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { MessageType } from '../../util/message-types';

type InputProps = {
  isAnnouncementThread: boolean;
  announcement: Announcement | null | undefined;
  announcementDeliverySummary: AnnouncementDeliverySummary | null | undefined;
  messageSid: string | undefined;
  isFailed: boolean | undefined;
  isScheduledToSend: boolean | undefined;
  messageType: MessageType | undefined;
};

const DeliverabilityIcon = ({
  isScheduled,
  percentFailed,
  hasPending,
}: {
  isScheduled: boolean;
  percentFailed: number;
  hasPending: boolean;
}) => {
  if (isScheduled) {
    return <MaterialDesignIcon size="sm" iconName={ICON_NAMES.SCHEDULE_SEND} />;
  }
  if (percentFailed > failedAnnouncementErrorThreshold) {
    return <MaterialDesignIcon size="sm" iconName={ICON_NAMES.INFO} />;
  }
  if (hasPending) {
    return <MaterialDesignIcon size="sm" iconName={ICON_NAMES.HOURGLASS} />;
  }
  return <MaterialDesignIcon size="sm" iconName={ICON_NAMES.CHECK} />;
};

export const MessageDeliverability = (props: InputProps) => {
  const {
    isAnnouncementThread,
    announcement,
    announcementDeliverySummary,
    messageSid,
    isFailed,
    isScheduledToSend,
    messageType,
  } = props;

  const [showMessageFailureDialog, setShowMessageFailureDialog] = useState(false);

  const handleMessageFailureClick = () => {
    if (isAnnouncementThread && announcement) {
      ProductTelemetryController.logEvent('clicked view announcement deliverability', {
        value: announcement.announcementId,
      });
    }

    setShowMessageFailureDialog(true);
  };
  const handleMessageFailureClose = () => setShowMessageFailureDialog(false);

  // If we have an individual message ie we are in an employee 1:1 thread
  if (!isAnnouncementThread || !announcement) {
    return (
      <div>
        {isFailed ? (
          <div
            role="presentation"
            className={`u-display-flex u-alignItems-flexEnd u-color-error ${
              messageSid ? 'u-cursor-pointer' : ''
            }`}
            onClick={messageSid ? handleMessageFailureClick : undefined}
          >
            <div className="u-margin-right-sm">Not Delivered</div>
            <MaterialDesignIcon size="sm" iconName={ICON_NAMES.INFO} />
          </div>
        ) : isScheduledToSend ? (
          <div className="u-display-flex u-alignItems-flexEnd">
            <div className="u-margin-right-sm">Scheduled</div>
            <MaterialDesignIcon size="sm" iconName={ICON_NAMES.SCHEDULE_SEND} />
          </div>
        ) : (
          <div className="u-display-flex u-alignItems-flexEnd">
            <div className="u-margin-right-sm">
              Delivered{messageType ? ` via ${humanize(messageType)}` : ''}
            </div>
            <MaterialDesignIcon size="sm" iconName={ICON_NAMES.CHECK} />
          </div>
        )}
        {messageSid && showMessageFailureDialog && (
          <TwilioErrorDetailDialog messageSid={messageSid} onClose={handleMessageFailureClose} />
        )}
      </div>
    );
  }

  // We are still loading in the delivery summary for this announcement
  if (!announcementDeliverySummary) {
    return null;
  }

  const successfulCount =
    announcementDeliverySummary.recipientCount -
    announcementDeliverySummary.failedCount -
    announcementDeliverySummary.pendingCount;
  const failedPercentage =
    (announcementDeliverySummary.failedCount / announcementDeliverySummary.recipientCount) * 100;

  const colorClass = failedPercentage > failedAnnouncementErrorThreshold ? 'u-color-error' : '';

  return (
    <div>
      <div
        className={`u-display-flex u-alignItems-flexEnd u-cursor-pointer typo-link-secondary ${colorClass}`}
      >
        <div
          role="presentation"
          className="u-margin-right-sm u-display-flex u-alignItems-flexEnd"
          onClick={handleMessageFailureClick}
        >
          {isScheduledToSend
            ? `${announcementDeliverySummary.recipientCount} Scheduled`
            : `${successfulCount} Delivered
             ${
               announcementDeliverySummary.pendingCount
                 ? `, ${announcementDeliverySummary.pendingCount} Pending`
                 : ''
             }
             ${
               announcementDeliverySummary.failedCount
                 ? `, ${announcementDeliverySummary.failedCount} Failed`
                 : ''
             }`}
          <DeliverabilityIcon
            isScheduled={!!isScheduledToSend}
            percentFailed={failedPercentage}
            hasPending={!!announcementDeliverySummary.pendingCount}
          />
        </div>
      </div>
      {showMessageFailureDialog ? (
        <AnnouncementStatusDialog
          announcementId={announcement.announcementId}
          onClose={handleMessageFailureClose}
        />
      ) : null}
    </div>
  );
};
